import type { MediaListItem } from './api';

/**
 * Układanie plików w katalogi serii.
 *
 * Wydzielone z ekranu, bo to **jedyna logika przeglądarki plików, która może się
 * pomylić po cichu**: zła sumaryczna wielkość albo odcinek w złym katalogu
 * wyglądają jak prawda. Reszta ekranu to rysowanie, które sprawdza się okiem.
 */

export type FileFilter = 'all' | 'upload' | 'fetched';
export type FileSortMode = 'recent' | 'name' | 'size';

export interface Folder {
  key: string;
  label: string;
  /** Okładka serii — miniatura katalogu. */
  coverUrl: string | null;
  /** `null` dla katalogu wgranych plików: nie prowadzi do żadnej serii. */
  titleId: string | null;
  episodesTotal: number | null;
  items: MediaListItem[];
  bytes: number;
  /** Najbliższe wygaśnięcie w katalogu — `null`, gdy wszystko przypięte. */
  soonestExpiry: string | null;
}

/** Nazwa materiału: nazwa pliku dla wgranych, numer odcinka dla pobranych. */
export function fileLabel(item: MediaListItem): string {
  if (item.originalName !== null && item.originalName.length > 0) return item.originalName;
  if (item.episodeNumber !== null) return `Odcinek ${item.episodeNumber}`;
  return 'Materiał';
}

/**
 * Układa materiały w katalogi.
 *
 * Klucz katalogu to `title_id`, a wgrane pliki mają własny — z punktu widzenia
 * porządkowania to osobna rzecz, nawet gdy ktoś wgrał odcinek serii, która jest
 * też w katalogu. Materiał pobrany bez tytułu (odcinek spoza katalogu) dostaje
 * trzeci worek, żeby nie zniknął z widoku.
 *
 * Wyeksportowane, bo pokrywamy to testem: układanie w katalogi jest jedyną
 * logiką na tym ekranie, która może się pomylić po cichu.
 */
export function groupIntoFolders(
  items: MediaListItem[],
  filter: FileFilter,
  query: string,
  sort: FileSortMode,
): Folder[] {
  const needle = query.trim().toLowerCase();

  const matching = items.filter((item) => {
    if (filter !== 'all' && item.kind !== filter) return false;
    if (needle.length === 0) return true;
    // Szukamy i po serii, i po nazwie pliku: przy pobranym odcinku nazwy pliku
    // często nie ma, a przy wgranym nie ma serii.
    const haystack = [item.titleRomaji, item.titleEnglish, item.originalName, fileLabel(item)]
      .filter((s): s is string => typeof s === 'string')
      .join(' ')
      .toLowerCase();
    return haystack.includes(needle);
  });

  const byKey = new Map<string, Folder>();
  for (const item of matching) {
    const key =
      item.kind === 'upload' ? 'upload' : item.titleId !== null ? item.titleId : 'fetched-other';

    let folder = byKey.get(key);
    if (folder === undefined) {
      folder = {
        key,
        label:
          key === 'upload'
            ? 'Wgrane pliki'
            : key === 'fetched-other'
              ? 'Pobrane bez serii'
              : (item.titleRomaji ?? item.titleEnglish ?? 'Nieznana seria'),
        coverUrl: key === 'upload' || key === 'fetched-other' ? null : item.titleCoverUrl,
        titleId: key === 'upload' || key === 'fetched-other' ? null : item.titleId,
        episodesTotal: item.titleEpisodesTotal,
        items: [],
        bytes: 0,
        soonestExpiry: null,
      };
      byKey.set(key, folder);
    }

    folder.items.push(item);
    folder.bytes += item.sizeBytes ?? 0;
    if (!item.pinned && item.expiresAt !== null) {
      if (folder.soonestExpiry === null || item.expiresAt < folder.soonestExpiry) {
        folder.soonestExpiry = item.expiresAt;
      }
    }
  }

  for (const folder of byKey.values()) {
    folder.items.sort((a, b) => {
      // Odcinki rosnąco — to kolejność oglądania, a nie pobierania. Wgrane pliki
      // nie mają numeru, więc dla nich zostaje najnowszy pierwszy (ULID malejąco).
      if (a.episodeNumber !== null && b.episodeNumber !== null) {
        return a.episodeNumber - b.episodeNumber;
      }
      return a.id < b.id ? 1 : -1;
    });
  }

  const folders = [...byKey.values()];
  folders.sort((a, b) => {
    if (sort === 'name') return a.label.localeCompare(b.label, 'pl');
    if (sort === 'size') return b.bytes - a.bytes;
    // „Ostatnio dodane" po najświeższym materiale w katalogu: ULID sortuje po
    // czasie utworzenia, więc maksimum identyfikatora wystarcza za datę.
    return newestId(b).localeCompare(newestId(a));
  });
  return folders;
}

function newestId(folder: Folder): string {
  let newest = '';
  for (const item of folder.items) if (item.id > newest) newest = item.id;
  return newest;
}

export function formatBytes(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(2)} GiB`;
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(0)} MiB`;
  return `${(bytes / 1024).toFixed(0)} KiB`;
}

/** Ile dni do wygaśnięcia, w formie znośnej do przeczytania. */
export function daysLeft(iso: string): string {
  const ms = Date.parse(iso) - Date.now();
  if (Number.isNaN(ms) || ms <= 0) return 'chwilę';
  const days = Math.floor(ms / 86_400_000);
  if (days === 0) return 'dziś';
  if (days === 1) return '1 dzień';
  return `${days} dni`;
}
